export type PreloadProgressCallback = (loadedCount: number, totalCount: number, url: string) => void;

export type PreloadOptions = {
	onProgress?: PreloadProgressCallback;
	cacheName?: string;
	// When true, skip failures instead of throwing (recommended for bulk preloads)
	ignoreErrors?: boolean;
};

/**
 * Preload and cache a list of asset URLs (images, videos, etc.) using the Cache Storage API.
 * Runs requests in parallel with per-URL progress updates.
 */
export async function preloadAssets(urls: string[], options: PreloadOptions = {}): Promise<void> {
	const uniqueUrls = Array.from(new Set(urls.filter(Boolean)));
	if (uniqueUrls.length === 0) return;

	const cacheName = options.cacheName ?? "static-asset-precache";
	const cache = await caches.open(cacheName);

	let loadedCount = 0;
	const notify = (url: string) => {
		loadedCount += 1;
		options.onProgress?.(loadedCount, uniqueUrls.length, url);
	};

	await Promise.all(
		uniqueUrls.map(async (url) => {
			try {
				// If already cached, count as loaded and skip fetch
				const existing = await cache.match(url, { ignoreSearch: true });
				if (existing) {
					notify(url);
					return;
				}
				// Use "no-store" to ensure we actually fetch and then place into Cache Storage
				const response = await fetch(url, { cache: "no-store" });
				// Only cache successful, basic/opaque responses
				if (response.ok || response.type === "opaque") {
					await cache.put(url, response.clone());
				}
				notify(url);
			} catch (error) {
				// Optionally ignore errors for robustness when some files are missing
				if (!options.ignoreErrors) {
					throw error;
				}
				notify(url);
			}
		})
	);
}

/**
 * Convenience preloader for images using HTMLImageElement to also prime decode.
 * Uses HTTP cache; optionally mirrors into Cache Storage for persistence.
 */
export async function preloadImages(
	urls: string[],
	options: PreloadOptions & { alsoCache?: boolean } = {}
): Promise<void> {
	const uniqueUrls = Array.from(new Set(urls.filter(Boolean)));
	if (uniqueUrls.length === 0) return;

	let loadedCount = 0;
	const notify = (url: string) => {
		loadedCount += 1;
		options.onProgress?.(loadedCount, uniqueUrls.length, url);
	};

	await Promise.all(
		uniqueUrls.map(
			(url) =>
				new Promise<void>((resolve) => {
					const img = new Image();
					img.onload = () => {
						notify(url);
						resolve();
					};
					img.onerror = () => {
						// Ignore individual failures by default
						if (!options.ignoreErrors) {
							// Still resolve to avoid blocking all
						}
						notify(url);
						resolve();
					};
					img.src = url;
				})
		)
	);

	if (options.alsoCache) {
		await preloadAssets(uniqueUrls, options);
	}
}


